import { Component, Input } from '@angular/core';
import { Product } from '../../../../../models/product.model';
import { OrderItem } from '../../../../../models/order-item.model';
import { SKU } from '../../../../../models/sku.model';
import { OrderItemSku } from '../../../../../models/order-item-sku.model';
import {
  computeAggregatedQuantity,
  computeProductPrice,
  computeGroupAggregatedQuantity,
  computeMinQuantity,
} from '../../../../../shared/helpers';
import { isEmptyArray } from '../../../../../shared/utils';

@Component({
  selector: 'app-product-order-item-price',
  templateUrl: './product-order-item-price.component.html',
  styleUrls: ['./product-order-item-price.component.scss'],
  standalone: false,
})
export class ProductOrderItemPriceComponent {
  private _orderItem: OrderItem;

  @Input() fontSize = ''; // normal (24px) | small (20px) |  tiny (16px)

  @Input() viewType: string;

  @Input() sku: SKU;

  @Input() product: Product;

  @Input() modal = false;

  @Input() hideUOM = false;

  @Input() componentClasses = '';

  @Input() set orderItem(val: OrderItem) {
    this._orderItem = val;
    if (this._orderItem) {
      this.product = this._orderItem.product;
    }
  }

  get price(): number {
    if (!this.product) {
      return 0;
    }

    if (this.hasVariations) {
      let skyQuantity = 0;
      if (this.product.isBuyingWindow) {
        skyQuantity = this.product.variationAggregatedQuantity || 1;
      } else {
        skyQuantity = this.getCombainedQtyForSku(this.sku) || 1;
      }
      return computeProductPrice(this.product, skyQuantity, this.sku);
    }
    return computeProductPrice(
      this.product,
      this.groupAggregatedQuantity || this.aggregatedQuantity
    );
  }

  get pkgQty(): string {
    if (!this.currentSKU) {
      return '';
    }

    const pkgQty = this.currentSKU.packageQuantity;
    if (pkgQty === 1) {
      return 'Each';
    } else if (pkgQty > 1) {
      return `${pkgQty} / Pack`;
    }

    return '';
  }

  get aggregatedQuantity(): number {
    let orderedQuantity = 0;

    if (!this._orderItem) {
      orderedQuantity = this.quantityDelta;
    }
    return computeAggregatedQuantity(this.product, this.sku) + orderedQuantity;
  }

  get groupAggregatedQuantity(): number {
    const groupMinQTY = computeMinQuantity(this.product);
    const groupAggregatedQuantity = computeGroupAggregatedQuantity(
      this.product
    );
    return groupMinQTY > 0 && groupAggregatedQuantity > 0
      ? groupAggregatedQuantity
      : 0;
  }

  get quantityForTiers(): number {
    if (this.product.hasVariations) {
      if (this.product.isBuyingWindow) {
        return this.product.variationAggregatedQuantity || 1;
      }
      return this.getCombainedQtyForSku(this.sku);
    }

    return this.groupAggregatedQuantity || this.aggregatedQuantity;
  }

  get orderedQuantity(): number {
    return this._orderItem ? this._orderItem.quantity : 0;
  }

  getCombainedQtyForSku(sku: SKU): number {
    if (!sku || isEmptyArray(this._orderItem?.deliveries)) {
      return 0;
    }

    return this._orderItem.deliveries
      .filter((d) => d.sku_id === sku.id)
      .reduce((sum, d) => sum + d.quantity, 0);
  }

  get discount(): number {
    if (this._orderItem) {
      return this._orderItem.productDiscount;
    }

    return this.product ? this.product.discount : 0;
  }

  get originalPrice(): number {
    return this.product.getOriginalPrice(this.sku);
  }

  get currentSKU() {
    return this.sku ? this.sku : this.product.skus[0];
  }

  // get orderItemSKU(): OrderItemSku {
  //   if (!this.sku || !this._orderItem) {
  //     return null;
  //   }
  //
  //   return this._orderItem.skus.find((s) => s.sku_id === this.sku.id);
  // }

  get classes(): string {
    return `${this.orientation} ${this.fontSize} ${this.componentClasses}`;
  }

  get orientation(): string {
    return this.viewType === 'grid-item' ? 'horizontal' : 'vertical';
  }

  get hasVariations(): boolean {
    return this.product.hasVariations;
  }

  get showPriceVariations(): boolean {
    return this.viewType === 'grid-item' && this.hasVariations;
  }

  get priceVariations(): { min?: number; max?: number } {
    return this.product.priceVariations;
  }

  get quantityDelta(): number {
    return this._orderItem?.quantityDelta || 0;
  }

  get canShowPriceTiers(): boolean {
    return !(this.product.hasVariations && this.viewType === 'grid-item');
  }

  get fontWeight(): 'normal' | 'bold' {
    return this.componentClasses.includes('fw-normal') ? 'normal' : 'bold';
  }
}
